import { ReactElement, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useQuery } from 'react-query'
import { Input, Modal, Select } from 'components'
import { TInstrument, TInstrumentWithId } from 'types/models'
import { useInstrumentTypesService } from 'services/instrumentsService'
import { ModalContainer, ModalForm } from '../../styles'

interface DeviceSNForm {
  serial_number: string
  instrument_type_id: number
  is_active: boolean
}

interface Props {
  onConfirm?(data: TInstrumentWithId | Partial<TInstrument>): Promise<void>
}

const useAddEditDeviceSNModal = ({ onConfirm }: Props = {}): [
  ReactElement,
  (deviceSN?: TInstrumentWithId) => void,
] => {
  const [isOpen, setIsOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [deviceSN, setDeviceSN] = useState<TInstrumentWithId | null>(null)

  const { getInstrumentTypes } = useInstrumentTypesService()

  const toggleModal = () => setIsOpen((v) => !v)

  const { data: instrumentTypesData } = useQuery({
    queryKey: ['instrument_types_all'],
    queryFn: () => getInstrumentTypes(),
    enabled: isOpen,
    retry: false,
  })

  const deviceTypes = useMemo(
    () =>
      (instrumentTypesData?.results || []).map((type) => ({
        value: type.id,
        label: `${type.name} (${type.manufacturer})`,
      })),
    [instrumentTypesData]
  )

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<DeviceSNForm>({
    defaultValues: {
      serial_number: '',
      instrument_type_id: undefined as unknown as number,
      is_active: true,
    },
  })

  useEffect(() => {
    if (deviceSN) {
      reset({
        serial_number: deviceSN.serial_number,
        instrument_type_id: deviceSN.instrument_type?.id ?? 0,
        is_active: deviceSN.is_active,
      })
    } else {
      reset({
        serial_number: '',
        instrument_type_id: undefined as unknown as number,
        is_active: true,
      })
    }
  }, [deviceSN, reset])

  const onSubmit = async (data: DeviceSNForm) => {
    const payload: Partial<TInstrument> = {
      serial_number: data.serial_number,
      instrument_type_id: data.instrument_type_id,
      is_active: data.is_active,
    }

    const finalPayload = deviceSN?.id
      ? ({ ...payload, id: deviceSN.id } as TInstrumentWithId)
      : payload

    setIsSubmitting(true)
    try {
      await onConfirm?.(finalPayload)
      toggleModal()
      reset()
    } catch {
    } finally {
      setIsSubmitting(false)
    }
  }

  const onClose = () => {
    if (isSubmitting) return
    toggleModal()
    reset()
  }

  return [
    <Modal
      isOpen={isOpen}
      title={deviceSN ? 'Edit Device SN' : 'Add Device SN'}
      okButtonType="submit"
      onClose={onClose}
      onConfirm={handleSubmit(onSubmit)}
      isLoading={isSubmitting}
    >
      <ModalForm onSubmit={handleSubmit(onSubmit)} id="hook-form">
        <ModalContainer>
          <Controller
            name="serial_number"
            control={control}
            rules={{ required: 'Serial Number is required' }}
            render={({ field }) => (
              <Input
                {...field}
                label="Serial Number"
                errorMsg={errors.serial_number?.message}
                fullWidth
                disabled={isSubmitting}
              />
            )}
          />

          <Controller
            name="instrument_type_id"
            control={control}
            rules={{ required: 'Device Type is required' }}
            render={({ field }) => (
              <Select
                {...field}
                value={field.value ?? ''}
                onChange={(e) => field.onChange(Number(e.target.value))}
                label="Device Type"
                options={deviceTypes}
                errorMsg={errors.instrument_type_id?.message}
                fullWidth
                disabled={isSubmitting}
              />
            )}
          />

          <Controller
            name="is_active"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                value={field.value ? 'true' : 'false'}
                onChange={(e) => field.onChange(e.target.value === 'true')}
                label="Status"
                options={[
                  { value: 'true', label: 'Active' },
                  { value: 'false', label: 'Inactive' },
                ]}
                fullWidth
                disabled={isSubmitting}
              />
            )}
          />
        </ModalContainer>
      </ModalForm>
    </Modal>,
    (data?: TInstrumentWithId) => {
      setDeviceSN(data || null)
      toggleModal()
    },
  ]
}

export default useAddEditDeviceSNModal
